/**
 * Сжатие фотографии перед отправкой.
 *
 * На устройстве, а не на сервере: телефон снимает кадр на 4–6 МБ, и
 * отправлять его целиком по мобильной связи ради того, чтобы сервер
 * уменьшил до 1600 пикселей, — это минута ожидания на каждой карточке.
 * Заодно не нужна нативная сборка `sharp`, разная под Windows и Linux.
 *
 * Превью отдельным файлом: сетка склада тянет двадцать картинок разом, и
 * полноразмерные там не нужны никому.
 */
const FULL_SIDE = 1600
const PREVIEW_SIDE = 320
const QUALITY = 0.85

/**
 * WebP при том же качестве весит вдвое меньше JPEG — на связи в подсобке
 * это разница между «отправилось» и «отправляется».
 *
 * Проверяем поддержку, а не полагаемся на неё: `toBlob` с неизвестным типом
 * молча отдаёт PNG, и снимки со старого WebView стали бы не меньше, а в
 * несколько раз больше. Считается один раз на загрузку страницы.
 */
let format: string | null = null

function pickFormat(): string {
  if (format) return format

  const probe = document.createElement('canvas')
  probe.width = 1
  probe.height = 1
  format = probe.toDataURL('image/webp').startsWith('data:image/webp') ? 'image/webp' : 'image/jpeg'

  return format
}

/** Расширение под выбранный формат: имя файла не должно врать про тип */
export const imageExtension = () => (pickFormat() === 'image/webp' ? 'webp' : 'jpg')

export interface CompressedImage {
  blob: Blob
  preview: Blob
  width: number
  height: number
}

/** Вписать в квадрат со стороной `side`, не растягивая мелкое */
function fit(width: number, height: number, side: number) {
  const scale = Math.min(1, side / Math.max(width, height))
  return { width: Math.round(width * scale), height: Math.round(height * scale) }
}

async function draw(source: ImageBitmap, side: number): Promise<Blob> {
  const size = fit(source.width, source.height, side)
  const canvas = document.createElement('canvas')
  canvas.width = size.width
  canvas.height = size.height

  const context = canvas.getContext('2d')
  if (!context) throw new Error('Не удалось подготовить изображение')

  context.drawImage(source, 0, 0, size.width, size.height)

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Не удалось сжать изображение'))),
      pickFormat(),
      QUALITY,
    )
  })
}

export async function compressImage(file: File | Blob): Promise<CompressedImage> {
  /* `createImageBitmap` разворачивает кадр по EXIF сам: без этого
     вертикальные снимки с телефона лягут набок */
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })

  try {
    const size = fit(bitmap.width, bitmap.height, FULL_SIDE)

    return {
      blob: await draw(bitmap, FULL_SIDE),
      preview: await draw(bitmap, PREVIEW_SIDE),
      width: size.width,
      height: size.height,
    }
  } finally {
    bitmap.close()
  }
}

/**
 * Файл строкой data-URL.
 *
 * Нужен там, где картинка уходит на сервер внутри JSON, а не отдельным
 * телом: аватар сотрудника и своё фото в профиле. Код был написан дважды —
 * одинаково, вплоть до имён переменных, — и это ровно тот случай, когда
 * копия не окупает даже собственной длины.
 *
 * Сжатия здесь нет намеренно: `compressImage` работает через canvas и
 * возвращает Blob, а тут нужна строка. Аватар маленький, и гнать его через
 * пережатие ради сотни килобайт значит усложнить путь без выигрыша.
 */
export function readAsDataUrl(file: File | Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}
